<template>
  <div class="volunteers-selector">
    <div class="actions">
      <vs-checkbox v-model="isAllCurrentPageSelected" @change="toggleSelectAllOnPage">
        تحديد الكل
      </vs-checkbox>
      <slot name="actions" />
      <vs-button icon-pack="feather" :icon="btnIcon" @click="onSubmit"
        :disabled="!selectedVolunteer.length && isSelectedIds">
        {{ btnLabel }}
      </vs-button>
    </div>

    <div class="vx-row justify-start mb-4">
      <div class="vx-col pb-1 sm:w-1/2 w-full" v-if="!hideGroupNamesFilter">
        <label class="mb-1 block">المجموعة</label>
        <multiselect v-model="selectedGroupNames" placeholder="اختر مجموعة" selectLabel="اضف هذه المجموعة"
          deselectLabel="احذف هذه المجموعة" :options="groupNameOptions" :loading="isLoadingGroups" multiple>
        </multiselect>
      </div>
      <div class="vx-col pb-1 sm:w-1/2 w-full" v-if="!hideCollageFilter">
        <label class="mb-1 block">الفرع الجامعي</label>
        <multiselect v-model="selectedCollages" tag-placeholder="اضف هذا الفرع" placeholder="اضف فرع جامعي جديد"
          deselectLabel="احذف هذا الفرع" :options="selectedCollages" multiple taggable @tag="addCollage">
        </multiselect>
      </div>
      <div class="vx-col pb-1 sm:w-1/2 w-full" v-if="!hideClassFilter">
        <label class="mb-1 block">الصف</label>
        <multiselect v-model="selectedClasses" tag-placeholder="اضف هذا الصف" placeholder="اضف صف جديد"
          deselectLabel="احذف هذا الصف" :options="selectedClasses" multiple taggable @tag="addClass">
        </multiselect>
      </div>
      <div class="vx-col pb-1 sm:w-1/2 w-full" v-if="!hideGenderFilter">
        <label class="mb-1 block">الجنس</label>
        <multiselect v-model="selectedGenders" :options="genderOptions" multiple placeholder="اختر الجنس"
          deselect-label="احذف">
        </multiselect>
      </div>
      <slot />
    </div>

    <ag-table :data-source-api="getAllVolunteers" :grid-options="gridOptions" :permissions="permissions" :rtl="false"
      :is-entities-response="false" />
  </div>
</template>

<script>
import { RepositoryFactory } from '../volunteer-repositories/volunteerRepositoryFactory';
import AgTable from '../../shared/shared-components/ag-grid/AgTableServerSide.vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

const volunteerRepo = RepositoryFactory.get('volunteerRepository');
const supervisorsRepo = RepositoryFactory.get('supervisorsRepository');

export default {
  name: "VolunteersSelector",
  props: {
    permissions: {
      type: Object,
      default: () => ({})
    },
    btnLabel: {
      type: String,
      default: 'حفظ التغيرات'
    },
    btnIcon: {
      type: String,
      default: ''
    },
    hideGroupNamesFilter: {
      type: Boolean,
      default: false
    },
    hideCollageFilter: {
      type: Boolean,
      default: false
    },
    hideClassFilter: {
      type: Boolean,
      default: false
    },
    hideGenderFilter: {
      type: Boolean,
      default: false
    },
    isSelectedIds: {
      type: Boolean,
      default: false
    },
    fullEntity: {
      type: Boolean,
      default: false
    },
    dataSourceApi: {
      type: Function,
      default: null
    },
    groupOptions: {
      type: Array,
      default: null
    }
  },
  components: {
    AgTable,
    Multiselect,
  },
  data() {
    return {
      gridApi: null,
      gridOptions: null,
      volunteerGroups: [],
      isLoadingGroups: false,
      selectedGroupNames: [],
      selectedCollages: [],
      selectedClasses: [],
      selectedGenders: [],
      filters: null,
      excludedVolunteer: [],
      selectedVolunteer: [],
      gender: {
        male: 'ذكر',
        female: 'انثى',
      },
      lastFilterState: {
        groupNames: [],
        collages: [],
        classes: [],
        genders: [],
      },
      revertingFilter: false,
      isBulkSelecting: false,
      /** Timeout IDs for delayed sync; cleared on destroy and when re-scheduling. */
      delayedSyncTimeouts: [],
    };
  },
  computed: {
    genderOptions() {
      return Object.values(this.gender);
    },
    groupNameOptions() {
      return this.groupOptions || this.volunteerGroups.map(group => group.name);
    },
    isAllCurrentPageSelected() {
      const currentPageNodes = this.getCurrentPageNodes();
      if (!currentPageNodes.length) return false;
      return currentPageNodes.every(node => node.isSelected());
    }
  },
  watch: {
    selectedGroupNames: {
      handler() {
        this.onAboveTableFilterChange();
      },
      deep: true
    },
    selectedCollages: {
      handler() {
        this.onAboveTableFilterChange();
      },
      deep: true
    },
    selectedClasses: {
      handler() {
        this.onAboveTableFilterChange();
      },
      deep: true
    },
    selectedGenders: {
      handler() {
        this.onAboveTableFilterChange();
      },
      deep: true
    }
  },
  methods: {
    createFilterQueryString(values, fieldPath) {
      if (!values || values.length === 0) return '';
      const encoded = values.map(v => encodeURIComponent(v)).join(',');
      return `filter=${fieldPath}||$in||${encoded}`;
    },
    createGroupNamesQueryString() {
      return this.createFilterQueryString(this.selectedGroupNames, 'volunteerInfo.groupName');
    },
    createCollagesQueryString() {
      return this.createFilterQueryString(this.selectedCollages, 'volunteerInfo.collage');
    },
    createClassesQueryString() {
      return this.createFilterQueryString(this.selectedClasses, 'volunteerInfo.studyYear');
    },
    createGenderQueryString() {
      return this.createFilterQueryString(this.selectedGenders, 'volunteerInfo.gender');
    },
    getAboveTableFilterParts() {
      return [
        this.createGroupNamesQueryString(),
        this.createCollagesQueryString(),
        this.createClassesQueryString(),
        this.createGenderQueryString(),
      ].filter(Boolean);
    },
    formatFilterPartsAsQuery(parts) {
      return parts.length ? `?${parts.join('&')}` : '';
    },
    async fetchVolunteerGroups() {
      this.isLoadingGroups = true;
      try {
        const response = await supervisorsRepo.fetchVolunteerGroups();
        this.volunteerGroups = response.data || [];
      } finally {
        this.isLoadingGroups = false;
      }
    },
    addCollage(collage) {
      this.selectedCollages.push(collage);
    },
    addClass(className) {
      this.selectedClasses.push(className);
    },
    onAboveTableFilterChange() {
      if (this.revertingFilter) return;
      const hasSelection = this.selectedVolunteer.length > 0 || this.excludedVolunteer.length > 0;
      if (hasSelection) {
        this.$vs.dialog({
          type: 'confirm',
          color: 'danger',
          title: 'تنبيه',
          text: 'سيتم إعادة تعيين ما قمت بتحديده. هل تريد المتابعة؟',
          acceptText: 'متابعة',
          cancelText: 'إلغاء',
          accept: () => {
            this.applyFilterChange();
          },
          cancel: () => {
            this.revertFilterState();
          },
        });
      } else {
        this.applyFilterChange();
      }
    },
    applyFilterChange() {
      this.selectedVolunteer = [];
      this.excludedVolunteer = [];
      if (this.gridApi) {
        this.gridApi.refreshServerSideStore({ purge: true });
      }
      this.saveFilterState();
    },
    saveFilterState() {
      this.lastFilterState = {
        groupNames: [...this.selectedGroupNames],
        collages: [...this.selectedCollages],
        classes: [...this.selectedClasses],
        genders: [...(this.selectedGenders || [])],
      };
    },
    revertFilterState() {
      this.revertingFilter = true;
      this.selectedGroupNames = [...this.lastFilterState.groupNames];
      this.selectedCollages = [...this.lastFilterState.collages];
      this.selectedClasses = [...this.lastFilterState.classes];
      this.selectedGenders = [...this.lastFilterState.genders];
      this.$nextTick(() => {
        this.revertingFilter = false;
      });
    },
    unselectAllRows() {
      if (this.gridApi) {
        this.gridApi.deselectAll();
      }
      this.selectedVolunteer = [];
      this.excludedVolunteer = [];
    },
    createColDefs() {
      return [
        {
          headerName: 'معرف المتطوع',
          field: 'id',
          sortable: true,
          suppressSizeToFit: true,
          checkboxSelection: true,
          width: 200,
        },
        {
          headerName: 'الاسم',
          field: 'name',
          filter: 'agTextColumnFilter',
          width: 210,
          sortable: false,
        },
        {
          headerName: 'كود اليسير',
          field: 'uniqueIdentifier',
          filter: 'agTextColumnFilter',
          width: 200,
          sortable: false
        },
        {
          headerName: 'رقم البطاقة',
          field: 'cardNumber',
          filter: 'agTextColumnFilter',
          width: 200,
          sortable: false
        },
        {
          headerName: 'رقم الموبايل',
          field: 'phoneNumber',
          filter: 'agTextColumnFilter',
          width: 210,
          sortable: false,
        },
        {
          headerName: 'المواليد',
          field: 'volunteerInfo.birthdate',
          filter: 'agDateColumnFilter',
          width: 210,
          sortable: false,
        },
        {
          headerName: 'المجموعة',
          field: 'volunteerInfo.groupName',
          filter: this.hideGroupNamesFilter ? 'agTextColumnFilter' : null,
          width: 210,
          sortable: false,
        },
        {
          headerName: 'الفرع الجامعي',
          field: 'volunteerInfo.collage',
          filter: this.hideCollageFilter ? 'agTextColumnFilter' : null,
          width: 210,
          sortable: false,
        },
        {
          headerName: 'الجنس',
          field: 'volunteerInfo.gender',
          width: 210,
          sortable: false,
          filter: this.hideGenderFilter ? 'agSetColumnFilter' : null,
          filterParams: this.hideGenderFilter ? { values: this.genderOptions } : undefined,
        },
        {
          headerName: 'الصف',
          field: 'volunteerInfo.studyYear',
          filter: this.hideClassFilter ? 'agTextColumnFilter' : null,
          width: 210,
          sortable: false,
        },
      ];
    },
    onSubmit() {
      this.$emit('onSubmit', {
        selectedVolunteer: this.selectedVolunteer,
        excludedVolunteer: this.excludedVolunteer,
        query: this.filters,
      });
    },
    async getAllVolunteers(query) {
      const ourFilters = this.getAboveTableFilterParts();
      let fullQuery = (query && String(query).startsWith('?')) ? String(query) : `?${query || ''}`;
      if (ourFilters.length) fullQuery += (fullQuery.length > 1 ? '&' : '') + ourFilters.join('&');

      this.filters = this.formatFilterPartsAsQuery(ourFilters);

      if (this.dataSourceApi) return await this.dataSourceApi(fullQuery);
      return await volunteerRepo.fetchVolunteers(fullQuery);
    },
    onRowSelected(event) {
      // When syncSelectionToDisplayedRows() calls node.setSelected(), the grid fires
      // onRowSelected for each node. We must ignore those: we're applying our arrays to
      // the UI, not the other way around. Otherwise we'd mutate selectedVolunteer/
      // excludedVolunteer from stale or duplicate events and corrupt the source of truth.
      if (this.isBulkSelecting) return;

      const volunteer = event.node.data;
      if (!volunteer) return;

      const volunteerId = volunteer.id;

      // ————————————— handle selected mode (isSelectedIds === true) —————————————
      if (this.isSelectedIds) {
        if (event.node.isSelected()) {
          if (!this.selectedVolunteer.some(v => (this.fullEntity ? v.id === volunteerId : v === volunteerId))) {
            this.selectedVolunteer.push(this.fullEntity ? volunteer : volunteerId);
          }
        } else {
          this.selectedVolunteer = this.selectedVolunteer.filter(v =>
            this.fullEntity ? v.id !== volunteerId : v !== volunteerId
          );
        }
      } else {
        // ————————————— handle excluded mode when isSelectedIds === false —————————————
        if (!event.node.isSelected()) {
          if (!this.excludedVolunteer.some(v => this.fullEntity ? v.id === volunteerId : v === volunteerId)) {
            this.excludedVolunteer.push(this.fullEntity ? volunteer : volunteerId);
          }
        } else {
          this.excludedVolunteer = this.excludedVolunteer.filter(v =>
            this.fullEntity ? v.id !== volunteerId : v !== volunteerId
          );
        }
      }
    },
    /**
     * Schedules sync to run after short delays so we catch rows that load asynchronously.
     * The grid uses server-side row model with block loading: rows can appear in multiple
     * batches (e.g. first paint then more after 100–500ms). Re-running sync at 150ms, 450ms,
     * and 900ms ensures newly loaded nodes get the correct checkbox state from
     * selectedVolunteer/excludedVolunteer. Cancels any previously scheduled runs.
     */
    scheduleDelayedSync() {
      this.clearDelayedSync();
      const delays = [150, 450, 900];
      this.delayedSyncTimeouts = delays.map((delay) =>
        setTimeout(() => {
          if (!this.gridApi) return;
          this.syncSelectionToDisplayedRows();
        }, delay)
      );
    },
    /** Clears any pending delayed sync timeouts (e.g. before re-scheduling or on destroy). */
    clearDelayedSync() {
      this.delayedSyncTimeouts.forEach((id) => clearTimeout(id));
      this.delayedSyncTimeouts = [];
    },
    getCurrentPageNodes() {
      if (!this.gridApi) return [];
      const nodes = [];
      const pageSize = this.gridApi.paginationGetPageSize();
      const currentPage = this.gridApi.paginationGetCurrentPage();
      const startIndex = currentPage * pageSize;
      const endIndex = startIndex + pageSize;

      this.gridApi.forEachNode((node) => {
        if (node.rowIndex >= startIndex && node.rowIndex < endIndex && node.data) {
          nodes.push(node);
        }
      });
      return nodes;
    },
    toggleSelectAllOnPage() {
      const nodes = this.getCurrentPageNodes();
      if (!nodes.length) return;

      const allSelected = nodes.every(node => node.isSelected());
      const shouldSelect = !allSelected;

      this.isBulkSelecting = true;
      try {
        nodes.forEach(node => {
          const volunteer = node.data;
          const volunteerId = volunteer.id;

          if (this.isSelectedIds) {
            if (shouldSelect) {
              if (!this.selectedVolunteer.some(v => this.fullEntity ? v.id === volunteerId : v === volunteerId)) {
                this.selectedVolunteer.push(this.fullEntity ? volunteer : volunteerId);
              }
            } else {
              this.selectedVolunteer = this.selectedVolunteer.filter(v =>
                this.fullEntity ? v.id !== volunteerId : v !== volunteerId
              );
            }
          } else {
            if (shouldSelect) {
              this.excludedVolunteer = this.excludedVolunteer.filter(v =>
                this.fullEntity ? v.id !== volunteerId : v !== volunteerId
              );
            } else {
              if (!this.excludedVolunteer.some(v => this.fullEntity ? v.id === volunteerId : v === volunteerId)) {
                this.excludedVolunteer.push(this.fullEntity ? volunteer : volunteerId);
              }
            }
          }
          node.setSelected(shouldSelect);
        });
      } finally {
        this.isBulkSelecting = false;
      }
    },
    /**
     * Applies our selection source of truth (selectedVolunteer / excludedVolunteer) to every
     * currently loaded row node. Needed because the grid uses server-side row model: rows
     * load in blocks and new nodes don't know their selected state. We only call
     * setSelected when the value would change to avoid redundant updates and flicker.
     */
    syncSelectionToDisplayedRows() {
      if (!this.gridApi) return;

      // Prevent onRowSelected from updating our arrays while we apply them to the grid.
      this.isBulkSelecting = true;

      try {
        const isSelected = (id) =>
          this.selectedVolunteer.some(v => (this.fullEntity ? v.id === id : v === id));
        const isExcluded = (id) =>
          this.excludedVolunteer.some(v => (this.fullEntity ? v.id === id : v === id));

        this.gridApi.forEachNode((node) => {
          if (!node.data) return;
          const id = node.data.id;
          const shouldSelect = this.isSelectedIds ? isSelected(id) : !isExcluded(id);
          if (node.isSelected() !== shouldSelect) {
            node.setSelected(shouldSelect);
          }
        });
      } finally {
        this.isBulkSelecting = false;
      }
    },
  },
  created() {
    this.gridOptions = {
      rowSelection: 'multiple',
      rowMultiSelectWithClick: true,
      suppressRowClickSelection: true,
      onRowSelected: this.onRowSelected,
      onPaginationChanged: () => {
        this.$nextTick(() => this.syncSelectionToDisplayedRows());
        this.scheduleDelayedSync();
      },
      onModelUpdated: () => {
        this.syncSelectionToDisplayedRows();
      },
      onGridReady: (params) => {
        this.gridApi = params.api;
      }
    };
    this.gridOptions.columnDefs = this.createColDefs();
    if (!this.hideGroupNamesFilter && !this.groupOptions) {
      this.fetchVolunteerGroups();
    }
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
  },
  beforeDestroy() {
    this.clearDelayedSync();
  }
};
</script>

<style>
.multiselect__option--highlight,
.multiselect__tag-icon:hover,
.multiselect__tag {
  background: rgb(75, 54, 78);
}

.multiselect__tag-icon:after {
  color: white;
}

.multiselect__option--highlight:after {
  background: rgb(75, 54, 78);
}

.actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}
</style>
